import React, { useEffect } from "react";
import { Redirect, useLocation } from "react-router";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import queryString from "query-string";
import Loader from "react-loader";

import globalActions from "actions/global";
import authenticationActions from "actions/tester_account/authentication";
import { testerApiPaths } from "configuration/api_paths";
import { testerAccountPaths } from "configuration/app_paths";
import authenticatedApiRequest from "app/api_request/authenticated_api_request";
import { SendApiRequestType } from "types/app";

const TrackerSSOGateway = React.lazy(() => import("components/tester_account/tracker_sso_gateway"));

export type AuthenticationFilterProps = {
  autoSignedIn: boolean;
  loginTokenChecked: boolean;
  authenticationToken: string;
  sendApiRequest: SendApiRequestType<{ login_token: string }, { authentication_token: string }>;
  globalActions: {
    cleanAlerts: () => void;
  };
  authenticationActions: {
    autoSignIn: () => void;
    signIn: (authenticationToken: string, rememberMe: boolean) => void;
    loginTokenChecked: () => void;
  };
  children: React.ReactNode;
};

const AuthenticationFilter = ({
  autoSignedIn,
  loginTokenChecked,
  authenticationToken,
  sendApiRequest,
  globalActions,
  authenticationActions,
  children,
}: AuthenticationFilterProps) => {
  const location = useLocation();

  useEffect(() => {
    globalActions.cleanAlerts();
    authenticationActions.autoSignIn();
  }, []);

  useEffect(() => {
    if (autoSignedIn && !loginTokenChecked) {
      checkLoginToken();
    }
  }, [autoSignedIn, loginTokenChecked]);

  const checkLoginToken = () => {
    const { loginToken } = queryString.parse(location.search);

    if (loginToken && typeof loginToken === "string") {
      signInWithLoginToken(loginToken);
    } else {
      authenticationActions.loginTokenChecked();
    }
  };

  const signInWithLoginToken = (loginToken: string) => {
    sendApiRequest({
      url: testerApiPaths.loginTokenSignIn,
      body: { login_token: loginToken },
      method: "POST",
      alert: false,
    }).then(
      ({ authentication_token }: { authentication_token: string }) => {
        if (authentication_token) {
          authenticationActions.signIn(authentication_token, false);
        }
        authenticationActions.loginTokenChecked();
      },
      () => {
        authenticationActions.loginTokenChecked();
      },
    );
  };

  const renderOrRedirect = () => {
    const isSignedIn = !!authenticationToken;
    const { returnTo, trackerReturnTo } = queryString.parse(location.search);

    if (!isSignedIn) {
      const params = queryString.stringify(trackerReturnTo ? { trackerReturnTo } : { returnTo: location.pathname });

      return <Redirect to={`${testerAccountPaths.signIn}?${params}`} />;
    }

    if (trackerReturnTo) {
      return <TrackerSSOGateway returnTo={trackerReturnTo as string} />;
    }

    if (returnTo && typeof returnTo === "string") {
      return <Redirect to={returnTo} />;
    }

    return children;
  };

  return <Loader loaded={loginTokenChecked}>{renderOrRedirect()}</Loader>;
};

type StateProps = {
  testerAccount: {
    authenticationToken: string;
    loginTokenChecked: boolean;
    autoSignedIn: boolean;
  };
};

function mapStateToProps({ testerAccount: { authenticationToken, loginTokenChecked, autoSignedIn } }: StateProps) {
  return {
    authenticationToken,
    loginTokenChecked,
    autoSignedIn,
  };
}

function mapDispatchToProps(dispatch: Dispatch<{ type: string }>) {
  return {
    authenticationActions: bindActionCreators(authenticationActions, dispatch),
    globalActions: bindActionCreators(globalActions, dispatch),
  };
}

export default authenticatedApiRequest(connect(mapStateToProps, mapDispatchToProps)(AuthenticationFilter));
